import ContactSection from "../../components/ContactForm";
import Header from "../../components/Header";
import { motion } from 'framer-motion';
import { FileText, Printer, Package, Layers, ArrowRight } from 'lucide-react';
import HeroSection from "../../components/HeroSection";
import ServicesSection from "../../components/Services";
import { Link, useLocation } from "react-router-dom";
import Footer from "../../components/Footer";
import { useEffect } from "react";
// import bannerImg from "../../assets/brochure-banner.png"; // Add your banner image path here

export default function BrochurePrinting() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  return (
    <div className="font-body">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-dark to-primary-base py-20 lg:py-32">
        <div className="absolute inset-0 bg-grid-white/[0.2] bg-[size:16px]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-5xl lg:text-6xl font-heading font-bold text-white leading-tight mb-8">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-accent-base to-accent-light">
                Brochure Printing
              </span>
            </h1>
            <p className="text-xl text-neutral-200 max-w-2xl mx-auto mb-8">
              Brochures are an essential tool for business marketing. Whether promoting your products or services, a well-designed brochure can effectively communicate your message to potential customers.
            </p>
            <Link to="/contact">
            <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl">
              Get Started Today
            </button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* What is Brochure Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-15"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              What is Brochure Printing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Brochures are an effective and affordable tool for advertising. They combine attractive images, informative content, and compelling headlines to engage potential customers. Perfect for promoting your business, product, or service.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Types of Brochures */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base text-4xl font-bold text-center text-primary-base mb-16">
            Types of Brochures
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Corporate Brochure</h3>
              <p className="text-gray-600">
                Corporate brochures are essential for businesses to promote their services and products, communicate their values, and attract potential customers.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Product Brochure</h3>
              <p className="text-gray-600">
                Ideal for businesses looking to showcase their range of products with clear, high-quality images and detailed descriptions.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Sales Brochure</h3>
              <p className="text-gray-600">
                Aimed at converting potential customers, sales brochures highlight offers, discounts, and persuasive details to drive conversions.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Institutional Brochure</h3>
              <p className="text-gray-600">
                Institutional brochures are used by schools, colleges, and organizations to inform about their services, values, and programs.
              </p>
            </motion.div>

            <motion.div 
              className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <h3 className="text-2xl font-bold text-primary-base mb-4">Service Brochure</h3>
              <p className="text-gray-600">
                Service brochures focus on a company's offerings, detailing how their services can solve a customer’s problem or meet their needs.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us for Brochure Printing */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center"
            {...fadeIn}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-primary-base mb-4">
              Why Choose Capital Offset for Brochure Printing?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              At Capital Offset, we pride ourselves on delivering high-quality brochures that meet your business needs. Our experience, skilled designers, and top-tier printing technology make us the leading brochure printing service in Ahmedabad.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Printer className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">High-Quality Printing</h3>
              <p className="text-gray-600">
                We use the best CMYK offset printing technology to ensure that your brochures have rich, vibrant colors and crisp text.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Package className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">Affordable Prices</h3>
              <p className="text-gray-600">
                We offer competitive pricing for brochure printing without compromising on quality.
              </p>
            </motion.div>

            <motion.div
              className="bg-neutral-50 p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <Layers className="w-12 h-12 text-primary-base mb-6" />
              <h3 className="text-2xl font-bold text-primary-base mb-4">Multiple Design Options</h3>
              <p className="text-gray-600">
                Choose from various templates or custom design your brochure with our expert graphic designers.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gradient-to-br from-primary-dark to-primary-base text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-5xl font-heading font-bold text-white text-4xl font-bold mb-8">Get Your Brochures Printed Today!</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Don’t let your first impression be a missed opportunity. Reach out to us today to get your professionally designed and printed Brochures!
          </p>
          <Link to="/contact">
          <button className="bg-accent-base hover:bg-accent-light text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 flex items-center mx-auto">
            Contact Us Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
          </Link>
        </div>
      </section>
<ServicesSection />
      {/* Contact Form */}
      <ContactSection />
      <Footer />
    </div>
  );
}
